import Head from "next/head";
import {
  useStoryblokState,
  getStoryblokApi,
  StoryblokComponent,
} from "@storyblok/react";
import LocationGrid from "../src/components/LocationGrid";
import { serializeJsonLd } from "../src/utilities/helpers";

const extractTextFromRichText = (node) => {
  if (!node) return "";

  if (Array.isArray(node)) {
    return node.map(extractTextFromRichText).join(" ").trim();
  }

  if (typeof node !== "object") return "";

  const currentText = typeof node.text === "string" ? node.text : "";
  const childText = node.content ? extractTextFromRichText(node.content) : "";

  return [currentText, childText].filter(Boolean).join(" ").trim();
};

const extractFaqItems = (node, acc = []) => {
  if (!node) return acc;

  if (Array.isArray(node)) {
    node.forEach((item) => extractFaqItems(item, acc));
    return acc;
  }

  if (typeof node !== "object") return acc;

  if (node.component === "accordionItem") {
    const question = typeof node.title === "string" ? node.title.trim() : "";
    const answer = extractTextFromRichText(node.body);

    if (question && answer) {
      acc.push({ question, answer });
    }
  }

  Object.values(node).forEach((value) => {
    if (value && typeof value === "object") {
      extractFaqItems(value, acc);
    }
  });

  return acc;
};

export default function Page({ story: intialStory, slug }) {
  const story = useStoryblokState(intialStory);
  const faqItems =
    slug === "find-a-therapist" ? extractFaqItems(story?.content) : [];

  const faqSchema =
    faqItems.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: faqItems.map(({ question, answer }) => ({
            "@type": "Question",
            name: question,
            acceptedAnswer: {
              "@type": "Answer",
              text: answer,
            },
          })),
        }
      : null;

  const findATherapistMetadata = {
    title: "Find a Therapist Near You | Welldoing | Trusted UK Counsellors & Therapists",
    description: "Find a therapist in the UK today. Search qualified counsellors by location, speciality or book online therapy with personalised matching.",
  }

  return (
    <div>
      <Head>
        <title>
          {story ? 
            slug === "find-a-therapist" ? findATherapistMetadata.title :
              `Welldoing - ${story.name}`
            : "Welldoing"
          }
        </title>
        <meta name="description" content={slug === "find-a-therapist" ? findATherapistMetadata.description : `Welldoing - ${story?.name}`} />
        <meta property="og:description" content={slug === "find-a-therapist" ? findATherapistMetadata.description : `Welldoing - ${story?.name}`} />
        <link rel="icon" href="/favicon.ico" />
        <link
          rel="canonical"
          href={`https://welldoing.org/${story.full_slug}`}
        />
        {faqSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: serializeJsonLd(faqSchema) }}
          />
        )}
      </Head>
      <div>
        <StoryblokComponent blok={story.content} />
      </div>
      {slug === "find-a-therapist" && (
        <>
          <LocationGrid />
        </>
      )}
    </div>
  );
}

/**
 * Walks the Storyblok content tree in document order and sets
 * _headingLevel = "h2" on every `title` blok after the first,
 * so only one H1 is rendered per page.
 */
function demoteDuplicateH1s(blok) {
  let foundFirst = false;

  function walk(node) {
    if (!node || typeof node !== "object") return;

    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }

    if (node.component === "title") {
      if (foundFirst) {
        node._headingLevel = "h2";
      }
      foundFirst = true;
    }

    for (const value of Object.values(node)) {
      if (value && typeof value === "object") walk(value);
    }
  }

  walk(blok);
}

export async function getStaticProps({ params, draftMode }) {
  let slug = (await params.slug) ? params.slug.join("/") : "home";
  let sbParams = {
    version: draftMode ? "draft" : "published",
  };

  const storyblokApi = getStoryblokApi();
  try {
    const { data } = await storyblokApi.get(`cdn/stories/${slug}`, sbParams, {
      cache: draftMode ? "no-cache" : undefined,
      next: {
        revalidate: draftMode ? undefined : 3600,
      },
    });
    var story = data.story;
  } catch (e) {
    console.error(`Cannot retrieve evergreen ${slug}`);
    console.error(e);
    return {
      notFound: true,
    };
  }

  if (slug === "types-of-therapy") {
    try {
      const {
        data: { stories },
      } = await storyblokApi.get(`cdn/stories`, {
        starts_with: "types",
        sort_by: "name:asc",
        per_page: 100,
      });
      const filteredTypes = stories.filter(
        (story) => story.slug !== "types-of-therapy"
      );
      story.content.body[2].types = filteredTypes;
      const hasItems = [];
      filteredTypes.forEach((item) => {
        hasItems.push(item.name[0].toLowerCase());
      });
      story.content.body[2].letterHasItems = hasItems;
    } catch (error) {
      console.error(e);
    }
  }

  if (slug === "whats-worrying-you") {
    try {
      const {
        data: { stories },
      } = await storyblokApi.get(`cdn/stories`, {
        starts_with: "therapy",
        sort_by: "name:asc",
        per_page: 100,
      });
      const filteredWorries = stories.filter(
        (story) => story.slug !== "whats-worrying-you"
      );
      story.content.body[2].worries = filteredWorries;
      const hasItems = [];
      filteredWorries.forEach((item) => {
        hasItems.push(item.name[0].toLowerCase());
      });
      story.content.body[2].letterHasItems = hasItems;
    } catch (error) {
      console.error(e);
    }
  }

  demoteDuplicateH1s(story.content);

  return {
    props: {
      draftMode: draftMode || null,
      story: story ?? false,
      story_id: story?.id ?? false,
      slug,
    },
    revalidate: draftMode ? undefined : 3600,
  };
}

export async function getStaticPaths() {
  const storyblokApi = getStoryblokApi();
  try {
    var pages = await storyblokApi.getAll("cdn/links", {
      version: "published",
      with_parent: "0",
      cv: "undefined",
    });
  } catch (e) {
    console.error(e);
    return {
      paths: [],
      fallback: "blocking",
    };
  }

  const paths = [];
  pages.forEach(({ is_folder, slug }) => {
    if (is_folder || slug === "home") {
      return;
    }

    paths.push({ params: { slug: slug.split("/") } });
  });

  return {
    paths: paths,
    fallback: "blocking",
  };
}
